import { PaginatedDataDTO, PaginationDTO, PaginationQueryParamsDTO } from '@bottega52/commons-pagination';
import { Button } from '@material-ui/core';
import { WithStyles, createStyles, withStyles } from '@material-ui/core/styles';
import WarningIcon from '@material-ui/icons/Warning';
import _ from 'lodash';
import moment from 'moment';
import 'moment/locale/it';
import React from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { ModalTypes } from '../../components/Modals/ModalTypes';
import Permission from '../../components/Permission/Permission';
import ReduxLanguage from '../../components/ReduxLanguage';
import * as ModalsActions from '../../redux/modals/modals.actions';
import * as SettingsActions from '../../redux/settings/settings.actions';
import { IState } from '../../redux/store';
import * as MksActions from '../../redux/mks/mks.actions';
import * as CreditsActions from '../../redux/credits/credits.actions';
import { IMKSRequestDTO, IMKSRequestInDTO, IMKSRequestParamsDTO, IMKSRequestStatusChangeDTO, IMKSRequestUpdateDTO } from '../../repository/jago/model/output/IMKSRequestDTO';
import AbilityProvider from '../../services/PermissionService/AbilityProvider';
import { PERMISSIONS } from '../../services/PermissionService/PermissionConstants';
import MKSRequestsTable, { MKSRequestStatusOptions, MKSRequestTypeOptions } from '../../components/MKS/MKSRequestsTable';
import MKSRequestSearchBar from '../../components/SearchBar/MKSRequestSearchBar';
import MKSRequestForm from '../../components/Forms/MKSRequestForm';
import CustomerDetailsView from '../../components/Customers/CustomerDetailsView';
import { IDomainOptionDTO } from '../../repository/jago/model/input/IDomainOptionDTO';

const styles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    padding: 0,
  },
  titleText: {
    fontWeight: 'bold',
  },
  header: {
    backgroundColor: 'white',
    width: '100%',
    zIndex: 200,
    padding: 10,
    paddingRight: 20,
    borderBottom: '2px solid #5AC0B1',
  },
  tableContainer: {
    display: 'flex',
    backgroundColor: 'white',
    height: 'calc(100vh - 170px)',
    width: '100%'
  },
});

type IReduxProps = ConnectedProps<typeof connector> & RouteComponentProps<any>;
export interface IMKSRequestsPageState {
  pageSize: number;
  isLoading: boolean;
  searchValues: IMKSRequestParamsDTO;
}

export interface IMKSRequestsPageProps extends WithStyles<typeof styles> {
  language: string;
  mksRequests: PaginatedDataDTO<IMKSRequestDTO>;
}

export type ComponentProps = IMKSRequestsPageProps & IReduxProps;

class MksRequestsPage extends React.Component<ComponentProps, IMKSRequestsPageState> {

  constructor(props: ComponentProps) {
    super(props);
    this.state = {
      pageSize: 100,
      isLoading: false,
      searchValues: {},
    }
  }

  async componentDidMount() {
    const { dispatch } = this.props;
    const { pageSize } = this.state;
    this.setState({ isLoading: true });
    try {
      await dispatch<any>(MksActions.fetchMKSRequests({ page: 0, pageSize: pageSize }));
      this.setState({ isLoading: false });
    } catch (error) {
      this.setState({ isLoading: false });
      dispatch<any>(ModalsActions.showModal(`ERROR_OPEN_MKS_REQUESTS`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'mks.openMKSRequestsError',
        }
      }));
    }
  }

  onFetchElementsOnPage(page: number) {
    const { pageSize } = this.state;
    this.onFetchMKSRequests(page, pageSize);
  }

  onPageSizeChange(pageSize: number) {
    this.setState({ pageSize }, () => {
      this.onFetchMKSRequests(0, pageSize);
    })
  }

  async onFetchMKSRequests(page: number = 0, pageSize: number = 100) {
    const { dispatch } = this.props;
    const { searchValues } = this.state;
    const params: IMKSRequestParamsDTO = { page, pageSize, ...searchValues };
    try {
      this.setState({ isLoading: true });
      await dispatch<any>(MksActions.fetchMKSRequests(params));
      this.setState({ isLoading: false });
    } catch (error) {
      this.setState({ isLoading: false });
      throw error;
    }
  }

  onCreateMKSRequestRequest(request: IMKSRequestInDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`CREATE_MKS_REQUEST_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'mks.newRequest',
        successMessageKey: 'mks.createMKSRequestConfirm',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onCreateMKSRequestConfirm(request),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`CREATE_MKS_REQUEST_CONFIRM_MODAL`)),
      }
    }));
  }

  async onCreateMKSRequestConfirm(request: IMKSRequestInDTO) {
    const { dispatch, mksRequests } = this.props;
    const { pageSize } = this.state;
    const pagination = mksRequests.pagination as PaginationDTO;
    try {
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      await dispatch<any>(MksActions.createMKSRequest(request));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.hideModal('MKS_REQUEST_FORM_VIEW'));
      dispatch<any>(ModalsActions.hideModal('CREATE_MKS_REQUEST_CONFIRM_MODAL'));
      this.onFetchMKSRequests(pagination.number, pageSize);
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_MKS_REQUEST_CREATE`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'mks.createMKSRequestError',
        }
      }));
    }
  }

  async onCreateAndSendMKSRequest(request: IMKSRequestInDTO) {
    const { dispatch, mksRequests } = this.props;
    const { pageSize } = this.state;
    const pagination = mksRequests.pagination as PaginationDTO;
    try {
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      // Crea la richiesta
      const createdRequest = await dispatch<any>(MksActions.createMKSRequest(request));
      // Se la creazione ha successo, cambia lo status a SENT
      if (createdRequest && createdRequest.id) {
        const statusChangeData: IMKSRequestStatusChangeDTO = {
          status: MKSRequestStatusOptions.SENT
        };
        await dispatch<any>(MksActions.changeMKSRequestStatus(createdRequest.id, statusChangeData));
      }
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.hideModal('MKS_REQUEST_FORM_VIEW'));
      this.onFetchMKSRequests(pagination.number, pageSize);
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_MKS_REQUEST_CREATE`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'mks.createMKSRequestError',
        }
      }));
    }
  }

  async onOpenMKSRequestForm(requestToEdit?: IMKSRequestDTO) {
    const { dispatch } = this.props;
    try {      
      const modalType = !requestToEdit || requestToEdit.status === 'DRAFT' ? 
        ModalTypes.OPERATIONAL_VIEW_MODAL_BACKDROP : 
        ModalTypes.OPERATIONAL_VIEW_MODAL;
      dispatch<any>(ModalsActions.showModal(`MKS_REQUEST_FORM_VIEW`, {
        modalType: modalType,
        modalProps: {
          content: (
            <MKSRequestForm
              request={requestToEdit}
              onCreateEditMKSRequest={request => requestToEdit ? this.onEditMKSRequestRequest(requestToEdit.id, request) : this.onCreateMKSRequestConfirm(request as IMKSRequestInDTO)}
              onCreateAndSendRequest={request => this.onCreateAndSendMKSRequest(request)}
              onReloadRequests={() => { this.onFetchElementsOnPage(0) }}
              onOpenCustomerInfo={(customerId: number) => this.onOpenCustomerInfo(customerId)}
            />
          ),
          titleMessageKey: requestToEdit ? 'mks.editRequest' : 'mks.newRequest',
        }
      }));
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
    }
  }

  onEditMKSRequestRequest(requestId: number, request: IMKSRequestUpdateDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`EDIT_MKS_REQUEST_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'mks.editRequest',
        successMessageKey: 'mks.editMKSRequestConfirm',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onEditMKSRequestConfirm(requestId, request),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`EDIT_MKS_REQUEST_CONFIRM_MODAL`)),
      }
    }));
  }

  async onEditMKSRequestConfirm(requestId: number, request: IMKSRequestUpdateDTO) {
    const { dispatch, mksRequests } = this.props;
    const { pageSize } = this.state;
    const pagination = mksRequests.pagination as PaginationDTO;
    try {
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      await dispatch<any>(MksActions.updateMKSRequest(requestId, request));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.hideModal('MKS_REQUEST_FORM_VIEW'));
      dispatch<any>(ModalsActions.hideModal('EDIT_MKS_REQUEST_CONFIRM_MODAL'));
      this.onFetchMKSRequests(pagination.number, pageSize);
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_MKS_REQUEST_EDIT`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'mks.editMKSRequestError',
        }
      }));
    }
  }

  public async onOpenCustomerInfo(customerId: number) {
    const { dispatch } = this.props;
    try {
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      const customer = await dispatch<any>(CreditsActions.fetchCustomerDetails(customerId));
      dispatch<any>(CreditsActions.setSelecteCustomer(customer));
      await dispatch<any>(CreditsActions.fetchCustomerWallets({pageSize: 200, customerId: customerId }));
      if (AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CUSTOMER_PLANTS_READ)) {
        await dispatch<any>(CreditsActions.fetchCustomerPlants({pageSize: 200, customerId: customerId }));
      }
      if (AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CONTRACTS_READ)) {
        await dispatch<any>(CreditsActions.fetchCustomerContracts({pageSize: 200, customerId: customerId }));
      }
      if (AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CONTRACTS_READ)) {
        await dispatch<any>(CreditsActions.fetchCustomerAllContractServices({pageSize: 200, customerId: customerId }));
      }
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`OPERATIONAL_CUSTOMER_MODAL`, {
        modalType: ModalTypes.OPERATIONAL_VIEW_MODAL,
        modalProps: {
          onClose: () => dispatch<any>(CreditsActions.setSelecteCustomer({})),
          content: (
            <CustomerDetailsView
              onEditCustomerData={() => {}}
              hideEditCustomer={true}
              hideShowCustomerDetails={true}
              hideDeleteCustomer={true}
            />
          ),
          titleMessageKey: 'forms.customerInfo',
        }
      }));
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
    }
  }

  onDeleteMKSRequestRequest(request: IMKSRequestDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`DELETE_MKS_REQUEST_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'forms.warning',
        successMessageKey: 'mks.deleteMKSRequestConfirm',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onDeleteMKSRequestConfirm(request),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`DELETE_MKS_REQUEST_CONFIRM_MODAL`)),
      }
    }));
  }

  async onDeleteMKSRequestConfirm(request: IMKSRequestDTO) {
    const { dispatch, mksRequests } = this.props;
    const { pageSize } = this.state;
    const pagination = mksRequests.pagination as PaginationDTO;
    try {
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      await dispatch<any>(MksActions.deleteMKSRequest(request.id));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.hideModal('DELETE_MKS_REQUEST_CONFIRM_MODAL'));
      this.onFetchMKSRequests(pagination.number, pageSize);
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_MKS_REQUEST_DELETE`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'mks.deleteMKSRequestError',
        }
      }));
    }
  }

  onChangeStatusRequest(request: IMKSRequestDTO, newStatus: string, deliveryDate?: number) {
    const { dispatch } = this.props;
    
    // Se si passa a ORDER_SHIPPED, apri dialog per deliveryDate
    if (newStatus === 'ORDER_SHIPPED') {
      dispatch<any>(ModalsActions.showModal(`MKS_DELIVERY_DATE_MODAL`, {
        modalType: ModalTypes.TEXTFIELD_MODAL,
        modalProps: {
          icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
          titleMessageKey: 'forms.warning',
          successMessageKey: 'mks.setDeliveryDateConfirm',
          initialText: moment().format('DD/MM/YYYY'),
          fieldLabel: 'mks.deliveryDate',
          confirmMessageKey: 'forms.confirm',
          cancelMessageKey: 'forms.cancel',
          onConfirm: (deliveryDateText: string) => {
            const deliveryDate = moment(deliveryDateText, 'DD/MM/YYYY').toDate().getTime();
            this.onChangeStatusConfirm(request.id, newStatus, deliveryDate);
          },
          onCancel: () => dispatch<any>(ModalsActions.hideModal(`MKS_DELIVERY_DATE_MODAL`)),
        }
      }));
    } else {
      // Per tutti gli altri stati, usa il dialog di conferma normale
      dispatch<any>(ModalsActions.showModal(`CHANGE_STATUS_MKS_REQUEST_CONFIRM_MODAL`, {
        modalType: ModalTypes.CONFIRM_MODAL,
        modalProps: {
          icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
          titleMessageKey: 'forms.warning',
          successMessageKey: 'mks.changeStatusMKSRequestConfirm',
          confirmMessageKey: 'forms.confirm',
          cancelMessageKey: 'forms.cancel',
          onConfirm: () => this.onChangeStatusConfirm(request.id, newStatus, deliveryDate),
          onCancel: () => dispatch<any>(ModalsActions.hideModal(`CHANGE_STATUS_MKS_REQUEST_CONFIRM_MODAL`)),
        }
      }));
    }
  }

  async onChangeStatusConfirm(requestId: number, newStatus: string, deliveryDate?: number) {
    const { dispatch, mksRequests } = this.props;
    const { pageSize } = this.state;
    const pagination = mksRequests.pagination as PaginationDTO;
    try {
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      const statusChange: IMKSRequestStatusChangeDTO = { status: newStatus, deliveryDate };
      await dispatch<any>(MksActions.changeMKSRequestStatus(requestId, statusChange));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      
      // Chiudi tutti i modal possibili
      dispatch<any>(ModalsActions.hideModal('CHANGE_STATUS_MKS_REQUEST_CONFIRM_MODAL'));
      dispatch<any>(ModalsActions.hideModal('MKS_DELIVERY_DATE_MODAL'));
      dispatch<any>(ModalsActions.hideModal('MKS_EDIT_DELIVERY_DATE_MODAL'));
      
      this.onFetchMKSRequests(pagination.number, pageSize);
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_MKS_REQUEST_CHANGE_STATUS`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'mks.changeMKSRequestStatusError',
        }
      }));
    }
  }

  onEditDeliveryDate(request: IMKSRequestDTO) {
    const { dispatch } = this.props;
      dispatch<any>(ModalsActions.showModal(`MKS_EDIT_DELIVERY_DATE_MODAL`, {
        modalType: ModalTypes.TEXTFIELD_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'forms.warning',
        successMessageKey: 'mks.editDeliveryDateConfirm',
        initialText: request.deliveryDate ? moment(request.deliveryDate).format('DD/MM/YYYY') : moment().format('DD/MM/YYYY'),
        fieldLabel: 'mks.deliveryDate',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: (deliveryDateText: string) => {
          const deliveryDate = moment(deliveryDateText, 'DD/MM/YYYY').toDate().getTime();
          this.onChangeStatusConfirm(request.id, request.status, deliveryDate);
        },
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`MKS_EDIT_DELIVERY_DATE_MODAL`)),
      }
    }));
  }

  onRejectRequest(request: IMKSRequestDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`MKS_REJECT_REQUEST_MODAL`, {
      modalType: ModalTypes.TEXTFIELD_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'red', fontSize: 50 }} />,
        titleMessageKey: 'forms.warning',
        successMessageKey: 'mks.rejectRequestConfirm',
        initialText: "",
        fieldLabel: 'mks.rejectedNotes',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: (rejectedNotes: string) => {
          this.onRejectRequestConfirm(request.id, rejectedNotes);
        },
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`MKS_REJECT_REQUEST_MODAL`)),
      }
    }));
  }

  async onRejectRequestConfirm(requestId: number, rejectedNotes: string) {
    const { dispatch, mksRequests } = this.props;
    const { pageSize } = this.state;
    const pagination = mksRequests.pagination as PaginationDTO;
    try {
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      // Prima cambia lo status a REJECTED
      await dispatch<any>(MksActions.changeMKSRequestStatus(requestId, { status: 'REJECTED' }));
      // Poi aggiorna con le rejectedNotes
      const updateData: IMKSRequestUpdateDTO = {
        data: { rejectedNotes }
      };
      await dispatch<any>(MksActions.updateMKSRequest(requestId, updateData));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.hideModal('MKS_REJECT_REQUEST_MODAL'));
      this.onFetchMKSRequests(pagination.number, pageSize);
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_MKS_REQUEST_REJECT`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'mks.errorRejectRequest',
        }
      }));
    }
  }

  async onSearchMKSRequests(searchValues: IMKSRequestParamsDTO) {
    const { pageSize } = this.state;
    this.setState({ searchValues }, () => this.onFetchMKSRequests(0, pageSize));
  }

  async onResetSearch() {
    const { pageSize } = this.state;
    this.setState({ searchValues: {} }, () => this.onFetchMKSRequests(0, pageSize));
  }

  exportMKSRequestsCSV() {
    const { dispatch } = this.props;
    const { searchValues } = this.state;
    dispatch<any>(MksActions.exportMKSRequests(searchValues));
  }

  render() {
    const { mksRequests, classes } = this.props;
    const cardsPagination = !_.isEmpty(mksRequests.pagination) ? (mksRequests.pagination as PaginationDTO) : null;

    return (
      <div>
        <div className={classes.header} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <MKSRequestSearchBar
            totalElements={cardsPagination ? cardsPagination.totalElements : ''}
            onCreateNewEntityButtonClick={() => this.onOpenMKSRequestForm()}
            onSearch={(searchValues: IMKSRequestParamsDTO) => this.onSearchMKSRequests(searchValues)}
            onResetSearch={() => this.onResetSearch()}
          />
          <Permission abilityHelper={AbilityProvider.getAbilityHelper()} permission={PERMISSIONS.MKS_EXPORT_REQUESTS}>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <Button
                variant="contained"
                style={{ backgroundColor: 'cornflowerblue', color: 'white', marginLeft: 10, fontWeight: 'bold' }}
                onClick={() => this.exportMKSRequestsCSV()}
              >
                <ReduxLanguage languageKey="mks.exportRequests" />
              </Button>
            </div>
          </Permission>
        </div>
        <div className={classes.tableContainer}>
          <MKSRequestsTable
            mksRequests={mksRequests}
            isLoading={this.state.isLoading}
            rowsPerPage={this.state.pageSize}
            onFetchElementsOnPage={page => this.onFetchElementsOnPage(page)}
            onPageSizeChange={pageSize => this.onPageSizeChange(pageSize)}
            onRowClick={(request) => this.onOpenMKSRequestForm(request)}
            onDeleteMKSRequestRequest={(request) => this.onDeleteMKSRequestRequest(request)}
            onChangeStatusRequest={(request, newStatus, deliveryDate) => this.onChangeStatusRequest(request, newStatus, deliveryDate)}
            onEditDeliveryDate={(request) => this.onEditDeliveryDate(request)}
            onRejectRequest={(request) => this.onRejectRequest(request)}
          />
        </div>
      </div>
    );
  }
}

function mapStateToProps(state: IState) {
  return {
    language: state.settings.language,
    mksRequests: state.mks.mksRequests.data,
  };
}

const connector = connect(mapStateToProps);

export default connector(withRouter(withStyles(styles)(MksRequestsPage)));
